/*
 * Hover micro-interactions (original mouse-over / mouse-out actions).
 *
 * Config-driven so the set of hovers is easy to read and extend. Each entry
 * tweens either the element itself or a child, on enter and back on leave,
 * using GSAP with the original durations.
 */
import type { gsap as GSAP } from 'gsap'

import { gsap, EASE } from './core'

interface HoverSpec {
    selector: string
    child?: string
    initial?: gsap.TweenVars
    enter: gsap.TweenVars
    leave: gsap.TweenVars
    duration?: number
}

const HOVERS: HoverSpec[] = [
    // Pill background scales + fades in behind links (a-21).
    {
        selector: '.nav-link',
        child: '.link-background',
        initial: { autoAlpha: 0, scale: 0.7 },
        enter: { autoAlpha: 1, scale: 1 },
        leave: { autoAlpha: 0, scale: 0.7 },
        duration: 0.4
    },
    {
        selector: '.social-media-link',
        child: '.link-background',
        initial: { autoAlpha: 0, scale: 0.7 },
        enter: { autoAlpha: 1, scale: 1 },
        leave: { autoAlpha: 0, scale: 0.7 },
        duration: 0.4
    },
    {
        selector: '.cart-button',
        child: '.link-background',
        initial: { autoAlpha: 0, scale: 0.7 },
        enter: { autoAlpha: 1, scale: 1 },
        leave: { autoAlpha: 0, scale: 0.7 },
        duration: 0.4
    },
    // Blog cards (a-23 / a-24): only the image zooms and the content box lifts.
    {
        selector: '.blog-item-link, .blog-item-link3',
        child: '.blog-item-image',
        enter: { scale: 1.05 },
        leave: { scale: 1 },
        duration: 0.7
    },
    {
        selector: '.blog-item-link, .blog-item-link3',
        child: '.blog-item-content',
        enter: { y: -10 },
        leave: { y: 0 },
        duration: 0.7
    },
    // Showcase grid cards (a-67 / a-68): the image zooms and the content box
    // slides down. Vertical scroll drift is handled separately by parallax.
    {
        selector: '.showcase-item-link',
        child: '.showcase-item-image',
        enter: { scale: 1.05 },
        leave: { scale: 1 },
        duration: 0.5
    },
    {
        selector: '.showcase-item-link',
        child: '.showcase-item-content',
        enter: { y: 20 },
        leave: { y: 0 },
        duration: 0.5
    },
    // Product image zoom (a-77).
    {
        selector: '.product-item-link',
        enter: { scale: 1.1 },
        leave: { scale: 1 },
        duration: 0.7
    }
]

export const initHover = (ctx: GSAP.Context, scope: HTMLElement): void => {
    ctx.add(() => {
        const cleanups: Array<() => void> = []

        HOVERS.forEach((spec) => {
            const hosts = gsap.utils.toArray<HTMLElement>(scope.querySelectorAll(spec.selector))

            hosts.forEach((host) => {
                const target: HTMLElement | null = spec.child
                    ? host.querySelector(spec.child)
                    : host
                if (!target) return

                if (spec.initial) gsap.set(target, spec.initial)

                const enter = () =>
                    gsap.to(target, { ...spec.enter, duration: spec.duration ?? 0.5, ease: EASE })
                const leave = () =>
                    gsap.to(target, { ...spec.leave, duration: spec.duration ?? 0.5, ease: EASE })

                host.addEventListener('mouseenter', enter)
                host.addEventListener('mouseleave', leave)

                cleanups.push(() => {
                    host.removeEventListener('mouseenter', enter)
                    host.removeEventListener('mouseleave', leave)
                })
            })
        })

        // Service cards (a-60 / a-61): the dark card bg fades in + scales to 1.1,
        // the text turns white, the summary + "Learn More" slide up into view, and
        // the icon swaps to its white variant. All 0.5s, default ease.
        gsap.utils.toArray<HTMLElement>(scope.querySelectorAll('.service-item-card-link')).forEach((card) => {
            const bg = card.querySelector<HTMLElement>('.service-item-card-bg')
            const content = card.querySelector<HTMLElement>('.service-item-content')
            const desc = card.querySelector<HTMLElement>('.service-item-description-content')
            const icon = card.querySelector<HTMLElement>('.service-card-item-image')
            const iconWhite = card.querySelector<HTMLElement>('.service-card-item-white-image')

            // Touch / small screens have no hover, so the card stays in its
            // resting state and the summary is shown by CSS instead.
            const canHover = () =>
                window.matchMedia('(hover: hover) and (pointer: fine)').matches && window.innerWidth > 991

            const enter = () => {
                if (!canHover()) return
                if (bg) gsap.to(bg, { opacity: 1, scale: 1.1, duration: 0.5, ease: EASE })
                if (content) gsap.to(content, { color: '#ffffff', duration: 0.5, ease: EASE })
                if (desc) gsap.to(desc, { opacity: 1, y: 0, duration: 0.5, ease: EASE })
                if (icon) gsap.to(icon, { opacity: 0, duration: 0.5, ease: EASE })
                if (iconWhite) gsap.to(iconWhite, { opacity: 1, duration: 0.5, ease: EASE })
            }
            const leave = () => {
                if (!canHover()) return
                if (bg) gsap.to(bg, { opacity: 0, scale: 1, duration: 0.5, ease: EASE })
                if (content) gsap.to(content, { color: 'rgb(22, 22, 22)', duration: 0.5, ease: EASE })
                if (desc) gsap.to(desc, { opacity: 0, y: 10, duration: 0.5, ease: EASE })
                if (icon) gsap.to(icon, { opacity: 1, duration: 0.5, ease: EASE })
                if (iconWhite) gsap.to(iconWhite, { opacity: 0, duration: 0.5, ease: EASE })
            }

            card.addEventListener('mouseenter', enter)
            card.addEventListener('mouseleave', leave)
            cleanups.push(() => {
                card.removeEventListener('mouseenter', enter)
                card.removeEventListener('mouseleave', leave)
            })
        })

        // Footer links (a / a-2): the underline wipes in from the left on hover
        // (0.7s) and wipes out to the right on leave (0.8s), then resets
        // off-screen left ready for the next hover.
        gsap.utils.toArray<HTMLElement>(scope.querySelectorAll('.footer-link')).forEach((link) => {
            const underline = link.querySelector<HTMLElement>('.link-block-underline')
            if (!underline) return

            gsap.set(underline, { xPercent: -101 })
            const enter = () => gsap.to(underline, { xPercent: 0, duration: 0.7, ease: EASE })
            const leave = () =>
                gsap.to(underline, {
                    xPercent: 101,
                    duration: 0.8,
                    ease: EASE,
                    onComplete: () => gsap.set(underline, { xPercent: -101 })
                })

            link.addEventListener('mouseenter', enter)
            link.addEventListener('mouseleave', leave)
            cleanups.push(() => {
                link.removeEventListener('mouseenter', enter)
                link.removeEventListener('mouseleave', leave)
            })
        })

        // Team members: the name/role/socials card sits at the bottom of the
        // tile and rises + fades in on hover (0.7s in / 0.5s out). This replaces
        // the imported a-65/a-66 3D flip, which belonged to the old dark scrim.
        gsap.utils.toArray<HTMLElement>(scope.querySelectorAll('.team-member-item')).forEach((item) => {
            const content = item.querySelector<HTMLElement>('.team-member-content')
            if (!content) return

            gsap.set(content, { opacity: 0, y: 16 })
            const enter = () =>
                gsap.to(content, { opacity: 1, y: 0, duration: 0.7, ease: EASE, overwrite: true })
            const leave = () =>
                gsap.to(content, { opacity: 0, y: 16, duration: 0.5, ease: EASE, overwrite: true })

            item.addEventListener('mouseenter', enter)
            item.addEventListener('mouseleave', leave)
            cleanups.push(() => {
                item.removeEventListener('mouseenter', enter)
                item.removeEventListener('mouseleave', leave)
            })
        })

        // Slider items (a-62 / a-63), used by both showcase sliders: the image
        // zooms + tilts and the content background fades in (0.7s in / 0.5s out).
        gsap.utils.toArray<HTMLElement>(scope.querySelectorAll('.slide-item-link')).forEach((card) => {
            const image = card.querySelector<HTMLElement>('.slide-image')
            const bg = card.querySelector<HTMLElement>('.slide-content-bg')

            const enter = () => {
                if (image) gsap.to(image, { scale: 1.1, rotate: 0.5, duration: 0.7, ease: EASE, overwrite: true })
                if (bg) gsap.to(bg, { opacity: 1, duration: 0.7, ease: EASE, overwrite: true })
            }
            const leave = () => {
                if (image) gsap.to(image, { scale: 1, rotate: 0, duration: 0.5, ease: EASE, overwrite: true })
                if (bg) gsap.to(bg, { opacity: 0, duration: 0.5, ease: EASE, overwrite: true })
            }

            card.addEventListener('mouseenter', enter)
            card.addEventListener('mouseleave', leave)
            cleanups.push(() => {
                card.removeEventListener('mouseenter', enter)
                card.removeEventListener('mouseleave', leave)
            })
        })

        return () => cleanups.forEach((fn) => fn())
    })
}
